type IconProps = { size?: number };

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/**
 * The navigation set, drawn from an Islamic vocabulary rather than the usual
 * house / list / chart: a mihrab for home, a lantern for logging, and the
 * four-part mark repeated for the report.
 */

export function HomeIcon({ size = 22 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
      <path d="M12 2.6v1.8" />
      <path d="M5 21V11.5a7 7 0 0 1 14 0V21" />
      <path d="M3.2 21h17.6" />
    </svg>
  );
}

export function ReportIcon({ size = 22 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
      <circle cx="7.6" cy="7.6" r="3.1" />
      <circle cx="16.4" cy="7.6" r="3.1" />
      <circle cx="7.6" cy="16.4" r="3.1" />
      <circle cx="16.4" cy="16.4" r="3.1" />
    </svg>
  );
}

export function GearIcon({ size = 20 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      {...stroke}
      strokeWidth={1.8}
      aria-hidden="true"
    >
      {/* teeth, not spokes: eight radial strokes around a hub is the same
          drawing as the sun in the day sky, a few pixels away in the header */}
      <path d="M21.5 10.4v3.2l-2.6-.5-1.2 3 2.1 1.5-2.2 2.2-1.5-2.1-3 1.2.5 2.6h-3.2l.5-2.6-3-1.2-1.5 2.1-2.2-2.2 2.1-1.5-1.2-3-2.6.5v-3.2l2.6.5 1.2-3-2.1-1.5 2.2-2.2 1.5 2.1 3-1.2-.5-2.6h3.2l-.5 2.6 3 1.2 1.5-2.1 2.2 2.2-2.1 1.5 1.2 3Z" />
      <circle cx="12" cy="12" r="3.1" />
    </svg>
  );
}

export function BackIcon({ size = 24 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
      <path d="M14.5 5 8 12l6.5 7" />
    </svg>
  );
}

/**
 * Points towards the end of the line by default, or towards the start with
 * `back`. Which physical direction that is depends on the writing direction,
 * so the mirroring lives in one CSS rule rather than in every caller.
 */
export function ChevronIcon({ size = 20, back = false }: IconProps & { back?: boolean }) {
  return (
    <svg
      className={back ? 'chev chev-back' : 'chev'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      {...stroke}
      strokeWidth="var(--sw)"
      aria-hidden="true"
    >
      <path d="M9.5 5 16 12l-6.5 7" />
    </svg>
  );
}

export function MinusIcon({ size = 26 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      {...stroke}
      strokeWidth={3}
      aria-hidden="true"
    >
      <path d="M5 12h14" />
    </svg>
  );
}

export function PlusIcon({ size = 26 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      {...stroke}
      strokeWidth={3}
      aria-hidden="true"
    >
      <path d="M5 12h14M12 5v14" />
    </svg>
  );
}

export function TickIcon({
  size = 17,
  colour = 'var(--outline)',
}: IconProps & { colour?: string }) {
  return (
    <svg className="tick" width={size} height={size} viewBox="0 0 18 18" aria-hidden="true">
      <path
        d="M3.5 9.4 l 3.6 3.6 L 14.5 5.2"
        fill="none"
        stroke={colour}
        strokeWidth="2.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function BookIcon({ size = 26 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 34 34" aria-hidden="true">
      <path
        d="M5 6h10a2 2 0 0 1 2 2v20a2 2 0 0 0-2-2H5V6Z"
        fill="none"
        stroke="var(--accent-ink)"
        strokeWidth="1.8"
      />
      <path
        d="M29 6H19a2 2 0 0 0-2 2v20a2 2 0 0 1 2-2h10V6Z"
        fill="none"
        stroke="var(--accent-ink)"
        strokeWidth="1.8"
      />
    </svg>
  );
}
